/**
 * Canonical optimistic cache-patch helper for activity status changes
 * (card l_Q1_9Vr_d8r — consolidation refactor).
 *
 * ONE pure function pair that every activity-status entry point uses to flip the
 * visible status synchronously, then reconcile with the server in the
 * background. This is the shared core behind:
 *   - useActivityStatusUpdate (desktop badge / sidebars / modals / PO popover / batch)
 *   - the field install page (client/field/install/InstallActivityPage.tsx),
 *     which keeps its own offline useFieldMutation transport but reuses THIS
 *     helper so the cache-patch semantics are identical everywhere.
 *
 * WHY THIS EXISTS (the storm bug): status writes used to fan out 5+ BROAD
 * invalidations (activitiesKeys.all, ['activities'], ['job-tracker'],
 * ['job-activity-feed'], ['lot-activities'], dashboard workflows) — with the
 * activity hooks running staleTime:0 + refetchOnMount:'always', every mounted
 * query app-wide refetched, twice, producing a ~10s stall on a <0.6s DB. The
 * fix is: patch the cache here (instant), and invalidate ONLY the two scoped
 * keys this write actually touches.
 *
 * These are pure-ish functions over a QueryClient (no React), so apply/rollback
 * is unit-testable without a mounted component or a live server.
 *
 * Idempotency (realtime self-echo): the fields patched here (status_id /
 * trig_activity_status_name / trig_activity_status_color / completed_date /
 * completed_by) are the SAME fields the writer's own postgres_changes UPDATE
 * carries; the realtime reducer merges by shallow spread, so re-applying the
 * server echo produces the identical row — no flicker, no extra refetch.
 */

import type { QueryClient } from '@tanstack/react-query';
import { startOfDay } from 'date-fns';

import { activitiesKeys } from '../../generated_hooks/generated-activities-hooks.js';

export interface ActivityStatusPatch {
  activityId: string;
  statusId: string;
  /** Display name of the target status (drives the visible label). */
  statusName?: string | null;
  /** Hex color of the target status (drives the visible badge color). */
  statusColor?: string | null;
  /**
   * Whether the target status is terminal (a "complete"). When true we set
   * completed_date/completed_by; when false we clear the completion fields.
   */
  isComplete: boolean;
  /** Current user id — stamped as completed_by when isComplete. */
  completedBy?: string | null;
  /** Whether the completion counts square footage (assigned activities only). */
  squareFootageClaimed?: boolean | null;
  /** Whether this completion was triggered by auto-complete cascade. */
  wasAutocompleted?: boolean | null;
  /** The activity's lot id — patches the lot's activities list entry too. */
  lotId?: string | null;
}

/**
 * Snapshot of the exact cache entries mutated, so we can restore them verbatim
 * on error. Only entries that actually existed are captured.
 */
export interface ActivityStatusSnapshot {
  activityId: string;
  detailKey: readonly unknown[];
  hadDetail: boolean;
  previousDetail: unknown;
  lotListKey: readonly unknown[] | null;
  hadLotList: boolean;
  previousLotList: unknown;
  /** Other cached activities lists that held the row (e.g. the org-wide /activities list). */
  otherLists: Array<{ key: readonly unknown[]; data: unknown }>;
}

/** Build the merge fragment applied to an activity row for a status change. */
export function activityStatusFields(patch: ActivityStatusPatch): Record<string, unknown> {
  const fields: Record<string, unknown> = {
    status_id: patch.statusId,
    trig_activity_status_name: patch.statusName ?? null,
    trig_activity_status_color: patch.statusColor ?? null,
  };
  if (patch.isComplete) {
    fields.completed_date = startOfDay(new Date()).toISOString();
    fields.completed_by = patch.completedBy ?? null;
    fields.square_footage_claimed = patch.squareFootageClaimed ?? false;
    fields.was_autocompleted = patch.wasAutocompleted ?? false;
  } else {
    // Moving back to a non-terminal status clears the completion fields.
    fields.completed_date = null;
    fields.completed_by = null;
    fields.square_footage_claimed = null;
    fields.was_autocompleted = null;
  }
  return fields;
}

/**
 * Apply the optimistic status change to:
 *   - activitiesKeys.detail(id): merge the new status fields onto the row.
 *   - activitiesKeys.list({ trig_area_lot_id: lotId }): replace the matching
 *     row in place (only if that list is already cached AND holds the row).
 *   - every OTHER cached activitiesKeys.lists() entry that holds the row (e.g.
 *     the org-wide /activities page list, activitiesKeys.list({})) — same
 *     in-place row replace, so a status change flips there too. Lists without
 *     the row are left untouched (no stampede, no invalidation).
 *
 * Returns a snapshot for rollbackActivityStatusPatch. Never creates a detail
 * row from nothing (a merge onto undefined is skipped) so we don't fabricate
 * data.
 */
export function applyActivityStatusPatch(
  queryClient: QueryClient,
  patch: ActivityStatusPatch,
): ActivityStatusSnapshot {
  const fields = activityStatusFields(patch);
  const detailKey = activitiesKeys.detail(patch.activityId);
  const previousDetail = queryClient.getQueryData(detailKey);
  const hadDetail = previousDetail !== undefined;

  if (hadDetail) {
    queryClient.setQueryData(detailKey, (old: any) => (old ? { ...old, ...fields } : old));
  }

  let lotListKey: readonly unknown[] | null = null;
  let previousLotList: unknown = undefined;
  let hadLotList = false;
  if (patch.lotId) {
    lotListKey = activitiesKeys.list({ trig_area_lot_id: patch.lotId });
    previousLotList = queryClient.getQueryData(lotListKey);
    hadLotList = previousLotList !== undefined;
    if (hadLotList) {
      queryClient.setQueryData(lotListKey, (old: any) => {
        if (!Array.isArray(old)) return old;
        let changed = false;
        const next = old.map((row: any) => {
          if (row?.id === patch.activityId) {
            changed = true;
            return { ...row, ...fields };
          }
          return row;
        });
        return changed ? next : old;
      });
    }
  }

  const lotListHash = lotListKey ? JSON.stringify(lotListKey) : null;
  const otherLists: Array<{ key: readonly unknown[]; data: unknown }> = [];
  for (const [key, data] of queryClient.getQueriesData({ queryKey: activitiesKeys.lists() })) {
    if (!Array.isArray(data) || JSON.stringify(key) === lotListHash) continue;
    if (!data.some((row: any) => row?.id === patch.activityId)) continue;
    otherLists.push({ key, data });
    queryClient.setQueryData(key, data.map((row: any) =>
      row?.id === patch.activityId ? { ...row, ...fields } : row,
    ));
  }

  return {
    activityId: patch.activityId,
    detailKey,
    hadDetail,
    previousDetail,
    lotListKey,
    hadLotList,
    previousLotList,
    otherLists,
  };
}

/** Restore the exact cache entries captured before the optimistic apply. */
export function rollbackActivityStatusPatch(
  queryClient: QueryClient,
  snapshot: ActivityStatusSnapshot,
): void {
  if (snapshot.hadDetail) {
    queryClient.setQueryData(snapshot.detailKey, snapshot.previousDetail);
  }
  if (snapshot.lotListKey && snapshot.hadLotList) {
    queryClient.setQueryData(snapshot.lotListKey, snapshot.previousLotList);
  }
  for (const { key, data } of snapshot.otherLists) {
    queryClient.setQueryData(key, data);
  }
}

/**
 * SCOPED, non-blocking reconcile. Invalidates ONLY the two keys this write
 * touched — the activity detail and the lot's activities list — NEVER
 * activitiesKeys.all / ['activities'] / ['job-tracker'] / ['job-activity-feed']
 * / ['lot-activities'] / dashboard workflows. With staleTime:0 +
 * refetchOnMount:'always' on the activity hooks, invalidating broad keys
 * stampedes every mounted query app-wide (the storm). invalidateQueries does
 * NOT clear cached data, and the optimistic patch already shows the new status,
 * so this refetch never blocks the UI. Not awaited by callers.
 */
export function invalidateActivityStatusScoped(
  queryClient: QueryClient,
  activityId: string,
  lotId?: string | null,
): void {
  void queryClient.invalidateQueries({ queryKey: activitiesKeys.detail(activityId) });
  if (lotId) {
    void queryClient.invalidateQueries({
      queryKey: activitiesKeys.list({ trig_area_lot_id: lotId }),
    });
  }
}
